import { CommentsNotLoggedIn } from '../Comment/CommentsNotLoggedIn.component';
import { CommentsTextarea } from '../Comment/CommentsTextArea.component';
import { SingleCommentLoader } from './CommentsListLoader.component';
import { Article, Comment as CommentApi, InlineResponse2002 } from 'pleinchamp-api-client';
import React, { ComponentProps, ComponentPropsWithRef, FC, forwardRef, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Comment } from '@components/content/Comment/Comment.component';
import { Text } from '@components/Text/Text.component';
import { RootState } from '@store/store';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { formatCount } from '@utils/number';
import { defaultExtendedPagination, ExtendedPagination, extendPagination } from '@utils/pagination';
import classnames from 'classnames';

import styles from './CommentsList.module.scss';

type Props = ComponentPropsWithRef<'div'> & {
  onCommentSubmit: (comment: string) => Promise<CommentApi | undefined>;
  loadComments: (offset: number, limit: number) => Promise<InlineResponse2002 | undefined>;
  contentType: Article | undefined;
};

const NB_COMMENTS_MOBILE = 3;
const NB_COMMENTS_DESKTOP = 5;

enum Status {
  initial,
  loading,
  loaded,
}

export const CommentsList: FC<Props> = forwardRef(
  ({ contentType, onCommentSubmit, loadComments, className, ...rest }, ref) => {
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const nbPerPage = useIsBreakpointDown(Breakpoint.m) ? NB_COMMENTS_MOBILE : NB_COMMENTS_DESKTOP;
    const [comments, setComments] = useState<CommentApi[]>([]);
    const [status, setStatus] = useState<Status>(Status.initial);
    const [pagination, setPagination] = useState<ExtendedPagination>({
      ...defaultExtendedPagination,
      limit: nbPerPage,
    });
    const [isHintVisible, setIsHintVisible] = useState<ComponentProps<typeof CommentsTextarea>['isHintVisible']>();
    const { count, current, offset, limit, hasNext } = pagination;
    const rootClass = classnames(styles['comments-list'], className);
    const formatedCount = formatCount(count);

    useEffect(() => {
      setStatus(Status.initial);
      setComments([]);
    }, [contentType]);

    useEffect(() => {
      setStatus(Status.initial);
      setComments([]);

      return () => {
        setStatus(Status.initial);
        setComments([]);
      };
    }, []);

    function resetCommentHint() {
      setIsHintVisible(undefined);
    }

    async function onCommentSubmitWrapper(comment: string) {
      try {
        const newComment = await onCommentSubmit(comment);
        if (newComment) {
          setIsHintVisible('success');
        } else {
          setIsHintVisible('error');
        }
      } catch (e: any) {
        setIsHintVisible('error');
      }
    }

    const loadCommentsWrapper = useCallback(
      async (newOffset: number, newLimit: number) => {
        setStatus(Status.loading);
        try {
          const response = await loadComments(newOffset, newLimit);
          if (response) {
            const { comments: newComments, pagination: newPagination } = response;
            setComments([...comments, ...newComments]);
            setPagination(extendPagination(newPagination));
          }
        } catch (e: any) {
          console.error('Error while fetching comments', e);
        } finally {
          setStatus(Status.loaded);
        }
      },
      [loadComments, comments]
    );

    async function seeMore() {
      if (hasNext) {
        loadCommentsWrapper(current, limit);
      }
    }

    useEffect(() => {
      if (status === Status.initial) {
        loadCommentsWrapper(offset, limit);
      }
    }, [loadCommentsWrapper, status, offset, limit]);

    return (
      <div ref={ref} className={rootClass} {...rest}>
        <Text
          className="plc-mb-m"
          i18nKey="common.comments-count"
          i18nOptions={{ count, formatedCount }}
          tag="p"
          variant="p-style-h3"
        />
        <div>
          {isAuthenticated ? (
            <CommentsTextarea
              isHintVisible={isHintVisible}
              onChange={resetCommentHint}
              onCommentSubmit={onCommentSubmitWrapper}
            />
          ) : (
            <CommentsNotLoggedIn className="plc-mb-l" />
          )}
        </div>
        <ul>
          {comments.map(comment => (
            <Comment key={comment.id} comment={comment} />
          ))}
          {status === Status.loading && (
            <>
              <SingleCommentLoader uniqueKey="singleComment-1" />
              <SingleCommentLoader uniqueKey="singleComment-2" />
              <SingleCommentLoader uniqueKey="singleComment-3" />
            </>
          )}
        </ul>
        {status === Status.loaded && hasNext && (
          <SecondaryButton onClick={seeMore} textProps={{ i18nKey: 'common.see-more' }} />
        )}
      </div>
    );
  }
);
